import React from "react";
import {
    Box,
    TextField,
    Button,
    IconButton,
    Typography,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Card,
    CardContent,
    Grid,
    FormControlLabel,
    Checkbox,
    alpha,
    Icon,
} from "@mui/material";
import { Modifier, ModifierGroup } from "../../../pos/types/pos.types";

interface Props {
    modifiers: ModifierGroup[];
    onChange: (groups: ModifierGroup[]) => void;
}

// Inline theme colors
const THEME_COLORS = {
    primary: "#0891B2",
    primaryHover: "#0E7490",
    danger: "#DC2626",
    text: {
        primary: "#1E293B",
        secondary: "#64748B",
    },
    border: {
        light: "rgba(8, 145, 178, 0.1)",
        main: "rgba(8, 145, 178, 0.2)",
    },
};

export const ModifierEditor: React.FC<Props> = ({ modifiers, onChange }) => {
    const addGroup = () => {
        const newGroup: ModifierGroup = {
            id: Date.now().toString(),
            name: "New Group",
            type: "checkbox",
            required: false,
            min_selection: 0,
            max_selection: 1,
            modifiers: [],
        };
        onChange([...modifiers, newGroup]);
    };

    const updateGroup = (
        index: number,
        field: keyof ModifierGroup,
        value: any
    ) => {
        const updatedGroups = [...modifiers];
        updatedGroups[index] = { ...updatedGroups[index], [field]: value };
        onChange(updatedGroups);
    };

    const deleteGroup = (index: number) => {
        const updatedGroups = [...modifiers];
        updatedGroups.splice(index, 1);
        onChange(updatedGroups);
    };

    const addModifier = (groupIndex: number) => {
        const updatedGroups = [...modifiers];
        const newMod: Modifier = {
            id: Date.now().toString(),
            name: "New Modifier",
            price: 0,
        };
        updatedGroups[groupIndex].modifiers.push(newMod);
        onChange(updatedGroups);
    };

    const updateModifier = (
        groupIndex: number,
        modIndex: number,
        field: keyof Modifier,
        value: any
    ) => {
        const updatedGroups = [...modifiers];
        updatedGroups[groupIndex].modifiers[modIndex] = {
            ...updatedGroups[groupIndex].modifiers[modIndex],
            [field]: value,
        };
        onChange(updatedGroups);
    };

    const deleteModifier = (groupIndex: number, modIndex: number) => {
        const updatedGroups = [...modifiers];
        updatedGroups[groupIndex].modifiers.splice(modIndex, 1);
        onChange(updatedGroups);
    };

    return (
        <Box sx={{ mt: 3 }}>
            <Typography
                variant="h6"
                gutterBottom
                sx={{
                    fontFamily: "Montserrat",
                    fontWeight: 600,
                    color: THEME_COLORS.text.primary,
                    mb: 2,
                }}
            >
                <Icon sx={{ color: THEME_COLORS.primary }}>settings</Icon>
                Add, Edit, and Delete menu items modifiers
            </Typography>

            {modifiers.map((group, groupIndex) => (
                <Card
                    key={group.id}
                    sx={{
                        mb: 3,
                        border: `1px solid ${THEME_COLORS.border.light}`,
                        borderRadius: 2,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    }}
                >
                    <CardContent sx={{ p: 3 }}>
                        <Grid container spacing={3}>
                            {/* Group Name */}
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Group Name"
                                    value={group.name}
                                    onChange={(e) =>
                                        updateGroup(groupIndex, "name", e.target.value)
                                    }
                                    placeholder="e.g., Toppings"
                                    InputProps={{
                                        startAdornment: (
                                            <Box
                                                component="span"
                                                sx={{
                                                    mr: 1,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    color: THEME_COLORS.primary,
                                                }}
                                            >
                                                <Typography variant="body2">🏷️</Typography>
                                            </Box>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: THEME_COLORS.primary,
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: THEME_COLORS.primary,
                                            },
                                        },
                                    }}
                                />
                            </Grid>

                            {/* Type */}
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Type</InputLabel>
                                    <Select
                                        value={group.type}
                                        label="Type"
                                        onChange={(e) =>
                                            updateGroup(groupIndex, "type", e.target.value)
                                        }
                                        startAdornment={
                                            <Box
                                                component="span"
                                                sx={{
                                                    mr: 1,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    color: THEME_COLORS.primary,
                                                }}
                                            >
                                                <Typography variant="body2">⚙️</Typography>
                                            </Box>
                                        }
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: THEME_COLORS.primary,
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: THEME_COLORS.primary,
                                                },
                                            },
                                        }}
                                    >
                                        <MenuItem value="radio">Radio (Single Choice)</MenuItem>
                                        <MenuItem value="checkbox">Checkbox (Multiple Choices)</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Min Selections */}
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Min Selections"
                                    type="number"
                                    value={group.min_selection}
                                    onChange={(e) =>
                                        updateGroup(
                                            groupIndex,
                                            "min_selection",
                                            parseInt(e.target.value) || 0
                                        )
                                    }
                                    InputProps={{
                                        startAdornment: (
                                            <Box
                                                component="span"
                                                sx={{
                                                    mr: 1,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    color: THEME_COLORS.primary,
                                                }}
                                            >
                                                <Typography variant="body2">🔽</Typography>
                                            </Box>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: THEME_COLORS.primary,
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: THEME_COLORS.primary,
                                            },
                                        },
                                    }}
                                />
                            </Grid>

                            {/* Max Selections */}
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Max Selections"
                                    type="number"
                                    value={group.max_selection}
                                    onChange={(e) =>
                                        updateGroup(
                                            groupIndex,
                                            "max_selection",
                                            parseInt(e.target.value) || 0
                                        )
                                    }
                                    InputProps={{
                                        startAdornment: (
                                            <Box
                                                component="span"
                                                sx={{
                                                    mr: 1,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    color: THEME_COLORS.primary,
                                                }}
                                            >
                                                <Typography variant="body2">🔼</Typography>
                                            </Box>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: THEME_COLORS.primary,
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: THEME_COLORS.primary,
                                            },
                                        },
                                    }}
                                />
                            </Grid>

                            {/* Required Toggle */}
                            <Grid item xs={12} md={4}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={group.required}
                                            onChange={(e) =>
                                                updateGroup(groupIndex, "required", e.target.checked)
                                            }
                                            sx={{
                                                color: THEME_COLORS.primary,
                                                '&.Mui-checked': {
                                                    color: THEME_COLORS.primary,
                                                },
                                            }}
                                        />
                                    }
                                    label="Required"
                                />
                            </Grid>

                            {/* Modifiers List */}
                            <Grid item xs={12}>
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        fontWeight: 600,
                                        mb: 2,
                                        color: THEME_COLORS.text.secondary,
                                    }}
                                >
                                    Modifiers
                                </Typography>
                                {group.modifiers.map((mod, modIndex) => (
                                    <Box
                                        key={mod.id}
                                        sx={{
                                            display: "flex",
                                            gap: 1,
                                            alignItems: "center",
                                            mb: 1,
                                        }}
                                    >
                                        <TextField
                                            label="Name"
                                            value={mod.name}
                                            onChange={(e) =>
                                                updateModifier(groupIndex, modIndex, "name", e.target.value)
                                            }
                                            size="small"
                                            sx={{
                                                flexGrow: 1,
                                                '& .MuiOutlinedInput-root': {
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: THEME_COLORS.primary,
                                                    },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: THEME_COLORS.primary,
                                                    },
                                                },
                                            }}
                                        />
                                        <TextField
                                            label="Price"
                                            type="number"
                                            value={mod.price}
                                            onChange={(e) =>
                                                updateModifier(
                                                    groupIndex,
                                                    modIndex,
                                                    "price",
                                                    parseFloat(e.target.value) || 0
                                                )
                                            }
                                            size="small"
                                            inputProps={{ step: 0.01 }}
                                            sx={{
                                                width: 100,
                                                '& .MuiOutlinedInput-root': {
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: THEME_COLORS.primary,
                                                    },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: THEME_COLORS.primary,
                                                    },
                                                },
                                            }}
                                        />
                                        <IconButton
                                            onClick={() => deleteModifier(groupIndex, modIndex)}
                                            color="error"
                                            size="small"
                                            sx={{
                                                bgcolor: alpha(THEME_COLORS.danger, 0.08),
                                                color: THEME_COLORS.danger,
                                                "&:hover": {
                                                    bgcolor: alpha(THEME_COLORS.danger, 0.12),
                                                },
                                            }}
                                        >
                                            ❌
                                        </IconButton>
                                    </Box>
                                ))}
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => addModifier(groupIndex)}
                                    startIcon={<span style={{ fontSize: "1rem" }}>➕</span>}
                                    sx={{
                                        borderColor: THEME_COLORS.border.main,
                                        color: THEME_COLORS.primary,
                                        "&:hover": {
                                            borderColor: THEME_COLORS.primary,
                                            bgcolor: alpha(THEME_COLORS.primary, 0.04),
                                        },
                                    }}
                                >
                                    Add Modifier
                                </Button>
                            </Grid>

                            {/* Delete Group Button */}
                            <Grid item xs={12}>
                                <Button
                                    color="error"
                                    onClick={() => deleteGroup(groupIndex)}
                                    fullWidth
                                    sx={{
                                        bgcolor: alpha(THEME_COLORS.danger, 0.04),
                                        color: THEME_COLORS.danger,
                                        "&:hover": {
                                            bgcolor: alpha(THEME_COLORS.danger, 0.08),
                                        },
                                    }}
                                >
                                    Delete Group
                                </Button>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            ))}

            {/* Add Group Button */}
            <Button
                variant="contained"
                onClick={addGroup}
                startIcon={<span style={{ fontSize: "1rem" }}>➕</span>}
                fullWidth
                sx={{
                    mt: 2,
                    bgcolor: THEME_COLORS.primary,
                    "&:hover": {
                        bgcolor: THEME_COLORS.primaryHover,
                    },
                }}
            >
                Add Modifier Group
            </Button>
        </Box>
    );
};